// packages import
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Grid
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { FC } from 'react'
import { Link } from 'react-router-dom'

// component import
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'

// others
import {
  Bill,
  UserType,
  ExternalUserCaseRole,
  CaseStatus,
  FetchDataForRecordPaymentsQuery
} from '../../../../generated'
import { capitalizeCase, formatCurrency, removeUnderScore } from '../../../utils'
import { formatStatus, statusColors } from '../../../utils/billing'
import palette from '../../../theme/palette'

type CaseFromQuery = NonNullable<
  NonNullable<FetchDataForRecordPaymentsQuery['fetchDataForRecordPayments']>['cases']
>[number]
type ClientFromQuery = NonNullable<NonNullable<FetchDataForRecordPaymentsQuery['fetchDataForRecordPayments']>['client']>

interface MatterBillsSectionProps {
  caseItem: CaseFromQuery
  client?: ClientFromQuery | null
  loading: boolean
}

export const MatterBillsSection: FC<MatterBillsSectionProps> = ({ caseItem, client, loading }): JSX.Element => {
  if (!caseItem) {
    return <></>
  }

  const { id, caseNumber, consultMatterNumber, title, status, billing, caseType, caseToUsers, bills } = caseItem
  const matterNumber = consultMatterNumber || caseNumber

  // Get attorney name
  const attorney = caseToUsers?.find(ctu => ctu?.userType === UserType.ResponsibleAttorney_1)?.user
  const attorneyName = attorney ? `${attorney.firstName} ${attorney.lastName}` : 'N/A'

  // Get primary client from case or use provided client
  const primaryClient =
    caseItem?.caseToExternalUserToExternalUserRole?.find(ctu => ctu?.caseRole === ExternalUserCaseRole.PrimaryClient)
      ?.externalUserToExternalUserRole?.externalUser || client

  const clientName = primaryClient ? (primaryClient.displayName || `${primaryClient.firstName || ''} ${primaryClient.lastName || ''}`.trim()) : 'N/A'
  const clientNumber = primaryClient?.clientNumber || 'N/A'

  const billsList = (bills || []).filter((bill): bill is Bill => bill !== null && bill !== undefined)

  const BILL_TABLE_HEADERS = [
    'Bill ID',
    'Issue Date',
    'Due Date',
    'Amount',
    'Paid',
    'Written Off',
    'Balance',
    'Status',
    'Actions'
  ]

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box width="100%">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Matter Number
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                <Link
                  to={`/matters/matter-detail/${id}`}
                  style={{
                    color: palette.primary.main,
                    textDecoration: 'none'
                  }}
                >
                  #{matterNumber}
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Title
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {title || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Status
              </Typography>
              <Chip
                label={capitalizeCase(status || '')}
                size="small"
                color={
                  status === CaseStatus.Active ? 'success' : status === CaseStatus.Completed ? 'default' : 'warning'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Attorney
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {attorneyName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Matter Type
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {caseType?.name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Billing Type
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {capitalizeCase(removeUnderScore(billing?.billingType?.toLowerCase() || '')) || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Client
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {clientName} {clientNumber !== 'N/A' && `(${clientNumber})`}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {billsList.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No bills found for this matter
            </Typography>
          </Box>
        ) : (
          <StickyTable height="400px">
            <TableHead>
              <TableRow>
                {BILL_TABLE_HEADERS.map((header, index) => (
                  <TableCell key={`${header}-${index}`} component="th">
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {loading ? (
              <TableSkeleton noOfRows={5} noOfCols={BILL_TABLE_HEADERS.length} />
            ) : (
              <TableBody>
                {billsList.map(bill => {
                  const { id: billId, amount, amountPaid, writtenOffPrincipal, status: billStatus } = bill || {}
                  const paid = amountPaid || 0
                  const writtenOff = writtenOffPrincipal || 0
                  const balance = amount - paid - writtenOff
                  const statusColor = statusColors[billStatus || ''] || '#9E9E9E'

                  return (
                    <TableRow key={billId} hover>
                      <TableCell>
                        <Link
                          to={`/billing/bill-detail/${billId}`}
                          state={{ isFinalBillView: true }}
                          style={{
                            color: palette.primary.main,
                            textDecoration: 'none'
                          }}
                        >
                          {billId}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {bill?.issueDate
                          ? new Date(bill.issueDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {bill?.dueDate
                          ? new Date(bill.dueDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{formatCurrency(amount || 0)}</TableCell>
                      <TableCell>{formatCurrency(paid)}</TableCell>
                      <TableCell>{formatCurrency(writtenOff)}</TableCell>
                      <TableCell>{formatCurrency(balance)}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatus(billStatus || '')}
                          size="small"
                          sx={{
                            color: statusColor,
                            border: `1px solid ${statusColor}50`,
                            backgroundColor: 'transparent'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/billing/bill-detail/${billId}`}
                          state={{ isFinalBillView: true }}
                          style={{
                            color: palette.primary.main,
                            textDecoration: 'none',
                            fontWeight: 500
                          }}
                        >
                          Record Payment
                        </Link>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            )}
          </StickyTable>
        )}
      </AccordionDetails>
    </Accordion>
  )
}
